<script lang="ts">
  import Layout from "$app/Layouts/Admin.svelte"
  import DataTable from "$app/components/DataTable.svelte"
  import { Pencil, Trash, Loader } from "lucide-svelte"
  import { formatDate, toast, xhr } from "$app/utils"
  import Form from "./Form.svelte"
  import Modal from "$app/components/Modal.svelte"

  let props = $props()
  let symbols: SymbolType[] = $state(props.symbols || [])
  let sort_field = $state(props.sort_field || "name")
  let sort_direction = $state(props.sort_direction || "asc")
  let limit: number = props.limit || 40
  let page = 1

  type symAndCount = SymbolType & {
    statements_count: number
  }
  let currentSymbol: symAndCount = $state({}) as symAndCount

  let loading = false
  let loadMore = $state(true)
  let status = $state("")
  let openModal = $state(false)
  let symbolName = $state("")

  function edit(symbol: SymbolType): void {
    currentSymbol = symbol as symAndCount
    status = "edit"
    openModal = true
  }

  async function setSort(sortV: any) {
    if (sort_field === sortV.key) {
      sort_direction = sort_direction === "asc" ? "desc" : "asc"
    } else {
      sort_field = sortV.key
      sort_direction = "asc"
    }

    const newSymbols = await getSearch(1)

    symbols = [...newSymbols]
  }

  async function loadMoreSymbols(): Promise<void> {
    page++
    try {
      loading = false
      const resp = await xhr.get(`/admin/plan_symbols.json`, {
        params: {
          q: "1",
          page: page,
          ...searchParams()
        }
      })
      loadMore = !(resp.data.symbols.length < limit)

      symbols = [...symbols, ...(resp.data.symbols as SymbolType[])]
    } catch (e) {
    } finally {
      loading = false
    }
  }

  let showingDelete = $state(false)
  async function openDeleteModal(item: SymbolType): Promise<void> {
    currentSymbol = {} as symAndCount
    showingDelete = true
    const resp = await xhr.get(`/admin/plan_symbols/${item.id}.json`)

    currentSymbol = resp.data.symbol as symAndCount
    currentSymbol.image_url = item.image_url
  }

  async function deleteSymbol(id: number) {
    try {
      await xhr.delete(`/admin/plan_symbols/${id}.json`)

      symbols = symbols.filter((s) => s.id !== id)
      currentSymbol = {} as symAndCount
      showingDelete = false
      toast("Symbol has been deleted", "success")
    } catch (e) {
      toast("There was an error deleting the symbols", "error")
    } finally {
      loading = false
    }
  }

  async function getSearch(p: number = 1): Promise<SymbolType[]> {
    try {
      loading = true
      const resp = await xhr.get(`/admin/plan_symbols.json`, {
        params: {
          q: "1",
          page: p,
          ...searchParams()
        }
      })

      return resp.data.symbols as SymbolType[]
    } catch (e) {
      toast("There was an error fetching the symbols", "error")
    } finally {
      loading = false
    }
  }

  async function search(): Promise<void> {
    const newSymbols = await getSearch(1)
    symbols = [...newSymbols]
  }

  function searchParams(): object {
    return {
      sort_field,
      sort_direction,
      name: symbolName
    }
  }

  function updateSymbol(data): void {
    const idx = symbols.findIndex((s) => s.id === data.id)
    if (idx > -1) {
      symbols[idx] = data
    }
    status = ""
  }

  const fields = [
    { key: "id", label: "ID", sorting: true },
    {
      key: "image_url",
      label: "Image",
      format: function (s: SymbolType) {
        return `<img src="${s.image_url}" class="h-24 w-auto mx-auto" />`
      }
    },
    { key: "name", label: "Name", sorting: true },
    //{ key: "description", label: "Description" },
    {
      key: "created_at",
      label: "Created on",
      format: (d) => formatDate(d.created_at),
      sorting: true
    },
    {
      key: "public_image",
      label: "Public",
      sorting: false
    }
  ]
</script>

<Layout {...props}>
  <span slot="title">Symbols</span>

  <div>
    <div class="flex gap-2 py-4">
      <div class="input-cont">
        <input
          type="text"
          class="!mt-0 text-gray-600 input"
          bind:value={symbolName}
          placeholder="Name"
        />
      </div>
      <button
        onclick={search}
        disabled={symbolName.trim() === ""}
        class="btn primary"
      >
        Search
      </button>

      <div class="w-full text-right">
        <button
          onclick={() => {
            status = "new"
            openModal = true
          }}
          class="btn primary"
        >
          New Symbol
        </button>
      </div>
    </div>
  </div>

  <DataTable
    {fields}
    sortField={sort_field}
    sortDirection={sort_direction}
    showNum={true}
    data={symbols}
    noValue={"<span class='text-gray-500'>No value</span>"}
    onsort={(e) => setSort(e)}
  >
    <svelte:fragment slot="actions" let:item>
      <div class="flex gap-4">
        <button onclick={() => edit(item)}>
          <Pencil class="text-blue-600" />
        </button>
        <button onclick={() => openDeleteModal(item)}>
          <Trash class="text-red-400" />
        </button>
      </div>
    </svelte:fragment>
  </DataTable>

  {#if loadMore}
    <div class="text-center p-4">
      <button onclick={loadMoreSymbols} class="btn primary"> Load More </button>
    </div>
  {/if}

  <Modal bind:open={openModal}>
    <Form
      symbol={currentSymbol}
      {status}
      onupdate={(symbol) => updateSymbol(symbol)}
      oncreate={(symbol) => {
        symbols = [symbol, ...symbols]
        status = ""
      }}
    />
  </Modal>

  <!-- Delete modal-->
  <Modal bind:open={showingDelete}>
    <div class="pt-4" style="width:350px">
      {#if !currentSymbol.id}
        <div class="flex text-gray-300">
          <Loader class="rotate" size="20" />
          <span class="ml-2">Loading...</span>
        </div>
      {:else}
        <h2 class="text-xl font-bold">Delete Symbol</h2>

        <div class="p-8">
          <img
            src={currentSymbol.image_url}
            class="w-full"
            alt={currentSymbol.name}
          />
        </div>

        <div>
          <span class="font-semibold text-gray-300">
            {currentSymbol.id}
          </span>
          <span class="font-semibold">{currentSymbol.name}</span> has :
          <span class="font-bold">{currentSymbol.statements_count}</span> Statements
        </div>

        {#if currentSymbol.statements_count > 0}
          <div class="mt-4 text-red-500 text-xl">
            You can't delete this symbol
          </div>
        {:else}
          <div class="py-2">
            <button
              class="btn danger w-full justify-items-center mt-2"
              onclick={() => deleteSymbol(currentSymbol.id)}
            >
              <span class="font-bold text-lg mx-auto"> DELETE </span>
            </button>
          </div>
        {/if}
      {/if}
    </div>
  </Modal>
</Layout>
